import * as types from "./actionTypes";
import * as queueApi from "../api/queueApi";
import { failureMessage, isForbidden, isNotFound } from "../api/client";
import { showInfo, showNotice } from "./noticeActions";
import { selectQueueSongs } from "../selectors";
import { clampSemitones } from "../lib/preferences";

let loadSequence = 0;

export const loadQueueSongs = (code) => async (dispatch) => {
  const sequence = ++loadSequence;
  try {
    const songs = await queueApi.getQueueSongs(code);
    if (sequence === loadSequence) dispatch({ type: types.QUEUE_LOAD_SUCCESS, songs });
  } catch (error) {
    console.error("Could not load the queue", error);
  }
};

// pushed by the server (and applied locally from the REST response)
export const queueSongAdded = (song) => ({ type: types.QUEUE_SONG_ADDED, song });
export const queueSongRemoved = (songId) => ({ type: types.QUEUE_SONG_REMOVED, songId });
export const queueCleared = () => ({ type: types.QUEUE_CLEARED });
export const queueReordered = (songIds) => ({ type: types.QUEUE_REORDERED, songIds });
export const queueSongSung = (songId, sung) => ({ type: types.QUEUE_SONG_SUNG, songId, sung });
export const queueSongTranspose = (songId, transpose) => ({ type: types.QUEUE_SONG_TRANSPOSE, songId, transpose });

const changeFailed = (error, what) =>
  showNotice(
    isForbidden(error)
      ? "Only the host can change the queue."
      : failureMessage(error, `Couldn't ${what}. Check your connection and try again.`)
  );

/** Moves a queued song one place up or down (host only). Applied optimistically. */
export const moveSong = (code, songId, direction) => async (dispatch, getState) => {
  const order = selectQueueSongs(getState()).map((song) => song.Id);
  const index = order.indexOf(songId);
  const target = index + (direction === "up" ? -1 : 1);
  if (index < 0 || target < 0 || target >= order.length) return;

  const next = [...order];
  [next[index], next[target]] = [next[target], next[index]];
  dispatch(queueReordered(next));
  try {
    dispatch(queueReordered(await queueApi.reorder(code, next)));
  } catch (error) {
    dispatch(changeFailed(error, "reorder the queue"));
    dispatch(loadQueueSongs(code));
  }
};

/** Marks a queued song as sung (skipped) or not (host only). Applied optimistically. */
export const setSongSung = (code, songId, sung) => async (dispatch) => {
  dispatch(queueSongSung(songId, sung));
  try {
    await queueApi.setSung(code, songId, sung);
  } catch (error) {
    dispatch(changeFailed(error, "update the song"));
    dispatch(loadQueueSongs(code));
  }
};

/** The key the room sings a queued song in (host only). Applied optimistically. */
export const setQueueTranspose = (code, songId, semitones) => async (dispatch, getState) => {
  const transpose = clampSemitones(semitones);
  if (!getState().songs[songId]) return;
  dispatch(queueSongTranspose(songId, transpose));
  try {
    await queueApi.setTranspose(code, songId, transpose);
  } catch (error) {
    dispatch(changeFailed(error, "change the key"));
    dispatch(loadQueueSongs(code));
  }
};

/** Empties the queue and deselects the current song (host only). */
export const clearQueue = (code) => async (dispatch) => {
  try {
    await queueApi.clearSongs(code);
    dispatch(queueCleared());
    dispatch({ type: types.SESSION_SET_SONG, SongId: null, VerseNum: -1 });
  } catch (error) {
    dispatch(changeFailed(error, "clear the queue"));
  }
};

export const addSongToQueue = (code, songId) => async (dispatch) => {
  try {
    const song = await queueApi.addSong(code, songId);
    dispatch(queueSongAdded(song));
    return song;
  } catch (error) {
    dispatch(
      isNotFound(error)
        ? showNotice("That song is no longer available.")
        : changeFailed(error, "add the song to the queue")
    );
    return null;
  }
};

/**
 * Queues a song right after the one on screens ("Next"), or at the end ("End"); with no
 * current song it goes to the end. Resolves with the queued song, or null when it failed.
 */
export const queueSongNext = (code, songId, { atEnd = false, semitones = 0, quiet = false } = {}) => async (dispatch, getState) => {
  const state = getState();
  const current = state.session.SongId;
  const alreadyQueued = Boolean(state.songs[songId]);
  try {
    const song =
      atEnd || !current || !state.songs[current]
        ? await queueApi.addSong(code, songId)
        : await queueApi.insertSong(code, songId, current);
    dispatch(queueSongAdded(song));
    if (!atEnd && current) dispatch(loadQueueSongs(code)); // the others' positions moved too
    const transpose = clampSemitones(semitones);
    if (transpose && !alreadyQueued) dispatch(setQueueTranspose(code, song.Id, transpose));
    if (!quiet) dispatch(showInfo(atEnd ? "Added to the end of the set" : "Queued next", { sub: song.Title }));
    return song;
  } catch (error) {
    dispatch(
      isNotFound(error)
        ? showNotice("That song is no longer available.")
        : changeFailed(error, "add the song to the set")
    );
    return null;
  }
};

export const removeSongFromQueue = (code, songId) => async (dispatch) => {
  try {
    await queueApi.removeSong(code, songId);
  } catch (error) {
    if (!isNotFound(error)) {
      dispatch(changeFailed(error, "remove the song from the queue"));
      return;
    }
  }
  dispatch(queueSongRemoved(songId));
};
